/**
 * AdminCorneliusConsolidations — Tier-2 consolidation viewer
 *
 * Lists each consolidation run (one row per gist) with parent count and a
 * one-click expand to see the raw memories that were consolidated.  The
 * consolidator (services/memory_consolidator.py) writes these hourly.
 */

import React, { useState, useEffect, useCallback } from 'react';

const cardStyle = (open) => ({
  background:   'var(--card-bg)',
  border:       `1px solid ${open ? 'var(--primary-color)' : 'var(--border-color)'}`,
  borderRadius: 'var(--border-radius)',
  padding:      '12px 14px',
  marginBottom: 10,
  color:        'var(--text-primary)',
  boxShadow:    'var(--shadow-sm)',
});

const inputStyle = {
  padding: '7px 12px', borderRadius: 'var(--border-radius)', border: '1px solid var(--border-color)',
  background: 'var(--surface-color)', color: 'var(--text-primary)', fontSize: 13, outline: 'none',
};

const btn = {
  padding: '7px 14px', borderRadius: 'var(--border-radius)', border: 'none',
  background: 'var(--primary-color)', color: 'var(--text-on-primary)',
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

function ParentRow({ p }) {
  return (
    <div style={{
      background: 'var(--surface-color)', border: '1px solid var(--border-color)',
      borderRadius: 'var(--border-radius)', padding: '6px 10px', marginBottom: 4,
      fontSize: 12, color: 'var(--text-primary)',
    }}>
      <div style={{ display: 'flex', gap: 8, fontSize: 11, color: 'var(--text-secondary)', marginBottom: 3 }}>
        <span>{p.memory_type}</span><span>imp {p.importance}</span>
        {p.source_tool && <span>via <code style={{ color: 'var(--primary-color)' }}>{p.source_tool}</code></span>}
        <span style={{ marginLeft: 'auto' }}>{p.created_at ? new Date(p.created_at).toLocaleString() : ''}</span>
      </div>
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{p.content}</div>
    </div>
  );
}

function ConsolidationRow({ c, userId }) {
  const [open,    setOpen]    = useState(false);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState('');

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && parents.length === 0) {
      setLoading(true); setErr('');
      try {
        const r = await fetch(`/api/opo/cornelius-memories/consolidations/${c.id}/parents?user_id=${userId}`);
        const data = await r.json();
        setParents(data.parents || []);
      } catch (e) { setErr(e.message); }
      setLoading(false);
    }
  }

  return (
    <div style={cardStyle(open)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={toggle}>
        <span style={{
          padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600,
          background: 'var(--surface-color)', border: '1px solid var(--border-color)',
          color: 'var(--primary-color)',
        }}>gist</span>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {c.parent_count} parent{c.parent_count === 1 ? '' : 's'}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          imp {c.importance} · conf {c.confidence != null ? `${Math.round(parseFloat(c.confidence)*100)}%` : '—'}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 'auto' }}>
          {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
        </span>
        <span style={{ color: 'var(--primary-color)', fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </div>

      <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-primary)',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {c.content}
      </div>

      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
        run <code>{c.consolidation_run_id ? String(c.consolidation_run_id).slice(0,8) : '—'}</code> ·
        via <code style={{ color: 'var(--primary-color)' }}>{c.source_tool || '—'}</code> ·
        memory id {c.id}
      </div>

      {open && (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--border-color)', paddingTop: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
            Raw memories this gist consolidated:
          </div>
          {err     && <div style={{ color: 'var(--danger-color)', fontSize: 12 }}>{err}</div>}
          {loading && <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Loading…</div>}
          {!loading && parents.length === 0 && !err && (
            <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No parent rows found.</div>
          )}
          {parents.map(p => <ParentRow key={p.id} p={p} />)}
        </div>
      )}
    </div>
  );
}

export default function AdminCorneliusConsolidations() {
  const [userId, setUserId]   = useState('13');
  const [rows,   setRows]     = useState([]);
  const [loading,setLoading]  = useState(false);
  const [error,  setError]    = useState('');

  const fetchRows = useCallback(async () => {
    if (!userId.trim()) { setRows([]); return; }
    setLoading(true); setError('');
    try {
      const r = await fetch(`/api/opo/cornelius-memories/consolidations?user_id=${userId.trim()}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setRows(data.consolidations || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [userId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: 13 }}>
        Recent memory consolidation runs (hourly). Each gist points back to the
        raw episodes it summarises — click any row to drill in.
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={userId} onChange={e=>setUserId(e.target.value)} onKeyDown={e=>e.key==='Enter'&&fetchRows()}
               placeholder="User ID" style={{ ...inputStyle, width: 100 }} />
        <button onClick={fetchRows} style={btn}>Refresh</button>
      </div>

      {error   && <div style={{ color: 'var(--danger-color)', fontSize: 13, marginBottom: 12 }}>Error: {error}</div>}
      {loading && <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>}
      {!loading && rows.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          No consolidations yet. The consolidator runs hourly and only fires when
          there are ≥3 unconsolidated episodic memories within the last 72h.
        </div>
      )}

      {rows.map(c => <ConsolidationRow key={c.id} c={c} userId={userId} />)}
    </div>
  );
}
